import { Box, TextField } from '@mui/material';
import React, { useContext, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { Types } from '../../enums/TypeEnums';
import {
  ICalendarEvent,
  ICompany,
  IContact,
  IMeetingType,
  useAddEventMutation,
  useCalendarTypesQuery,
  useCompaniesNamesQuery,
  useContactBasicDataQuery,
  useUpdateEventMutation
} from '../../generated/graphql';
import { addEventToCache, parseDate } from '../../utils/clientUtils';
import { canUserEdit } from '../../utils/rightUtils';
import { Colors } from '../../utils/theme';
import CancelButton from '../Common/Buttons/CancelButton';
import EditButton from '../Common/Buttons/EditButton';
import SaveButton from '../Common/Buttons/SaveButton';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import InputComponent from '../Common/Fields/InputComponent';
import DialogBox from '../Common/Layout/DialogBox';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraTimePicker from '../Common/Fields/EpraTimePicker';

interface IErrors {
  calendarTitle: string | null;
  calendarLocation: string | null;
  calendarCompany: string | null;
  calendarCreator: string | null;
  calendarStartDate: string | null;
  calendarEndDate: string | null;
  calendarType: string | null;
  startDate: string | null;
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
}

const defaultErrorsObj: IErrors = {
  calendarTitle: null,
  calendarLocation: null,
  calendarCompany: null,
  calendarCreator: null,
  calendarStartDate: null,
  calendarEndDate: null,
  calendarType: null,
  startDate: null,
  startTime: null,
  endDate: null,
  endTime: null
};

export const ErrorTexts = {
  calendarTitle: 'Title field is required',
  calendarCompany: 'Please select an option from the menu',
  calendarCreator: 'Please select creator option from the menu',
  calendarStartDate: 'Please select Starting date',
  calendarEndDate: 'Please select End date',
  calendarType: 'Please select Calendar Type',
  dateInPast: 'Selected date should not be in the past',
  timeInPast: 'Selected time should not be in the past',
  startDate: 'Select Start date',
  startTime: 'Select Time',
  endDate: 'Select End Date',
  endTime: 'Select Time',
  endTimeBeforeStartTime: 'End time should be after start time',
  startTimeInPast: 'Selected time should not be in the past',
  endDateAfterStart: 'End date should be after start date'
};

interface IProps {
  handleClose: () => void;
  isEditMode?: boolean;
  calendarEvent: ICalendarEvent | null;
}

const CalendarForm: React.FunctionComponent<IProps> = ({
  handleClose,
  isEditMode,
  calendarEvent
}: IProps) => {
  const { state, dispatch } = useContext(AppContext);
  const allCompanies = useCompaniesNamesQuery();
  const allCreators = useContactBasicDataQuery();
  const allTypes = useCalendarTypesQuery();
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(
    calendarEvent?.company ?? null
  );
  const [selectedCreator, setSelectedCreator] = useState<IContact | null>(
    calendarEvent?.creator ?? null
  );
  const [selectedType, setSelectedType] = useState<IMeetingType | null>(
    calendarEvent?.type ?? null
  );
  const [title, setTitle] = useState(calendarEvent?.title ?? '');
  const [location, setLocation] = useState(calendarEvent?.location ?? '');
  const [startDate, setStartDate] = useState<Date | null>(
    isEditMode ? parseDate(calendarEvent?.start_date) : null
  );
  const [startTime, setStartTime] = useState<Date | null>(
    isEditMode ? parseDate(calendarEvent?.start_date) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    isEditMode ? parseDate(calendarEvent?.end_date) : null
  );
  const [endTime, setEndTime] = useState<Date | null>(
    isEditMode ? parseDate(calendarEvent?.end_date) : null
  );
  const [description, setDescription] = useState(calendarEvent?.description ?? '');
  const [addEvent, addEventResult] = useAddEventMutation();
  const [updateEvent, updateEventResult] = useUpdateEventMutation();
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [editEnabled, setEditEnabled] = useState(calendarEvent === null);
  const canEdit = canUserEdit(state.user, Types.CalendarEvent);

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value.length > 1) {
      setFormErrors({ ...formErrors, calendarTitle: null });
    } else {
      setFormErrors({ ...formErrors, calendarTitle: ErrorTexts.calendarTitle });
    }
    setTitle(value);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    setDescription(value);
  };

  const handleLocationChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    setLocation(value);
  };

  const handleCalendarCompanyChange = (
    _: unknown,
    newValue: React.SetStateAction<ICompany | null>
  ) => {
    if (newValue) {
      setFormErrors({ ...formErrors, calendarCompany: null });
    } else {
      setFormErrors({ ...formErrors, calendarCompany: ErrorTexts.calendarCompany });
    }
    setSelectedCompany(newValue);
  };

  const handleCalendarCreatorChange = (
    _: unknown,
    newValue: React.SetStateAction<IContact | null>
  ) => {
    if (newValue) {
      setFormErrors({ ...formErrors, calendarCreator: null });
    } else {
      setFormErrors({ ...formErrors, calendarCreator: ErrorTexts.calendarCreator });
    }
    setSelectedCreator(newValue);
  };

  const handleCalendarTypeChange = (
    _: unknown,
    newValue: React.SetStateAction<IMeetingType | null>
  ) => {
    if (newValue) {
      setFormErrors({ ...formErrors, calendarType: null });
    } else {
      setFormErrors({ ...formErrors, calendarType: ErrorTexts.calendarType });
    }
    setSelectedType(newValue);
  };

  const handleStartDateChange = (dateInput: Date | null) => {
    let errors = formErrors;
    errors = { ...errors, startDate: null };
    if (dateInput) {
      if (startTime) {
        if (getCurrentDateTime(dateInput, startTime).getTime() < new Date().getTime()) {
          errors = { ...errors, startTime: ErrorTexts.startTimeInPast };
        }
      }

      if (endDate) {
        if (endDate < dateInput) {
          errors = { ...errors, endDate: ErrorTexts.endDateAfterStart };
        } else {
          errors = { ...errors, endDate: null };
        }
        if (endTime) {
          if (
            getCurrentDateTime(dateInput, startTime).getTime() >
            getCurrentDateTime(endDate, endTime).getTime()
          ) {
            errors = { ...errors, endTime: ErrorTexts.endTimeBeforeStartTime };
            errors = { ...errors, endDate: null };
          }
        } else {
          errors = { ...errors, endTime: null };
        }
      }
    } else {
      errors = { ...errors, startDate: ErrorTexts.startDate };
    }
    setFormErrors(errors);
    setStartDate(dateInput);
  };

  const handleStartTimeChanged = (timeInput: Date | null) => {
    let errors = formErrors;
    errors = { ...errors, startTime: null };
    if (timeInput) {
      if (startDate) {
        if (getCurrentDateTime(startDate, timeInput).getTime() < new Date().getTime()) {
          errors = { ...errors, startTime: ErrorTexts.startTimeInPast };
        }
        if (endDate && endTime) {
          if (
            getCurrentDateTime(startDate, timeInput).getTime() >
            getCurrentDateTime(endDate, endTime).getTime()
          ) {
            errors = { ...errors, endTime: ErrorTexts.endTimeBeforeStartTime };
          }
        }
      }
    }
    setFormErrors(errors);
    setStartTime(timeInput);
  };

  const handleEndDateChange = (dateInput: Date | null) => {
    let errors = formErrors;
    errors = { ...errors, endDate: null, endTime: null };
    if (dateInput) {
      if (startDate) {
        if (
          getCurrentDateTime(startDate, startTime).getTime() >
          getCurrentDateTime(dateInput, endTime).getTime()
        ) {
          errors = { ...errors, endTime: ErrorTexts.endTimeBeforeStartTime };
        }
      }
    } else {
      errors = { ...errors, endDate: ErrorTexts.endDate };
    }
    setFormErrors(errors);
    setEndDate(dateInput);
  };

  const handleEndTimeChange = (timeInput: Date | null) => {
    let errors = formErrors;
    errors = { ...errors, endTime: null };
    if (timeInput) {
      if (startDate && endDate) {
        if (
          getCurrentDateTime(startDate, startTime).getTime() >
          getCurrentDateTime(endDate, timeInput).getTime()
        ) {
          errors = { ...errors, endTime: ErrorTexts.endTimeBeforeStartTime };
        }
      }
    }

    setFormErrors(errors);
    setEndTime(timeInput);
  };

  const handleCreateButton = () => {
    const errors = {
      calendarTitle: null,
      calendarLocation: null,
      calendarCompany: null,
      calendarCreator: null,
      calendarStartDate: null,
      calendarEndDate: null,
      calendarType: null,
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null
    } as IErrors;
    let hasErrors = false;
    if (!title || title.length < 2) {
      hasErrors = true;
      errors.calendarTitle = 'Title is too short!';
    }
    if (!selectedCompany) {
      hasErrors = true;
      errors.calendarCompany = 'Company field is required!';
    }
    if (!selectedCreator) {
      hasErrors = true;
      errors.calendarCreator = 'Creator field is required!';
    }
    if (!selectedType) {
      hasErrors = true;
      errors.calendarType = 'Type field is required!';
    }
    if (!startDate) {
      hasErrors = true;
      errors.startDate = 'Start Date field is required!';
    }
    if (!endDate) {
      hasErrors = true;
      errors.endDate = 'End Date field is required!';
    }
    if (startDate && endDate) {
      if (startDate?.getTime() > endDate?.getTime()) {
        hasErrors = true;
        errors.startDate = 'Start Date should be before End Date';
        errors.endDate = 'Start Date should be before End Date';
      }
      if (startTime && endTime) {
        const start = getCurrentDateTime(startDate, startTime).getTime();
        const end = getCurrentDateTime(endDate, endTime).getTime();
        if (start > end) {
          hasErrors = true;
          errors.endDate = 'End Date should be after Start Date';
          errors.endTime = 'End Date should be after Start Date';
        } else {
          errors.endDate = null;
          errors.endTime = null;
        }
      }
    }

    if (hasErrors) {
      setFormErrors({ ...errors });
    } else {
      handleAddNewEvent();
    }
  };

  const handleAddNewEvent = () => {
    if (isEditMode) {
      updateEvent({
        variables: {
          id: parseInt(calendarEvent?.id as string),
          input: getCalendarDto()
        },
        update() {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Successfully updated ${title}`,
              severity: 'success'
            }
          });
          handleClose();
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: e.message,
            severity: 'error',
            autoHide: 6000
          }
        });
      });
    } else {
      addEvent({
        variables: {
          input: getCalendarDto()
        },
        update(_, { data }) {
          if (data?.result) {
            addEventToCache(data?.result as ICalendarEvent);
          }
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Event ${data?.result.title} created`,
              severity: 'success'
            }
          });
          handleClose();
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: e.message,
            severity: 'error',
            autoHide: 6000
          }
        });
      });
    }
  };

  const getCalendarDto = () => {
    return {
      title: title,
      location: location,
      company_id: parseInt(selectedCompany?.id as string),
      creator_id: parseInt(selectedCreator?.id as string),
      type_id: parseInt(selectedType?.id as string),
      description: description,
      start_date: getUTCDateAndTime(startDate as Date, startTime).toString(),
      end_date: getUTCDateAndTime(endDate as Date, endTime).toString()
    };
  };

  const getUTCDateAndTime = (date: Date, time: Date | null) => {
    const res = new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        time ? time.getHours() : 0,
        time ? time.getMinutes() : 0,
        0,
        0
      )
    );
    return res;
  };

  const getCurrentDateTime = (date: Date, time: Date | null) => {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time ? time.getHours() : 0,
      time ? time.getMinutes() : 0,
      0,
      0
    );
  };

  const handleCancel = () => {
    if (editEnabled && isEditMode && calendarEvent) {
      setTitle(calendarEvent.title);
      setLocation(calendarEvent.location ?? '');
      setSelectedCompany(calendarEvent.company);
      setSelectedCreator(calendarEvent.creator);
      setSelectedType(calendarEvent.type);
      setDescription(calendarEvent.description ?? '');
      setStartDate(parseDate(calendarEvent.start_date));
      setEditEnabled(false);
      setFormErrors(defaultErrorsObj);
    } else {
      handleClose();
    }
  };

  return (
    <DialogBox
      id="new-calendar-meeting-dialog"
      width={689}
      height={860}
      title={isEditMode ? 'Event Details' : 'Create Event'}
      onClose={handleClose}
    >
      <Box sx={{ padding: '80px', position: 'relative' }}>
        {isEditMode && (
          <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
            <EditButton
              id="create-event-edit-button"
              onClick={() => setEditEnabled(true)}
              disabled={!canEdit || editEnabled || !calendarEvent}
              editButtonColor={
                !canEdit || editEnabled || !calendarEvent ? Colors.gray : Colors.skyBlue
              }
            />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexFlow: 'column',
            rowGap: '35px',
            mt: '27px',
            mX: '37px'
          }}
        >
          <InputComponent
            id="title-name"
            label="Title"
            value={title}
            onChange={handleTitleChange}
            errorMsg={formErrors.calendarTitle}
            disabled={isEditMode && !editEnabled}
          />
          <InputComponent
            id="calendar-location"
            label="Location"
            value={location}
            onChange={handleLocationChange}
            disabled={isEditMode && !editEnabled}
          />
          <AutocompleteComponent
            id="calendar-creator-autocomplete"
            options={(allCompanies.data?.result as never[]) || []}
            getOptionLabel={(option: ICompany) => option.name as string}
            values={selectedCompany}
            onChange={handleCalendarCompanyChange}
            errorMsg={formErrors.calendarCompany}
            label="Company"
            disabled={isEditMode && !editEnabled}
          />
          <AutocompleteComponent
            id="calendar-company-autocomplete"
            options={(allCreators.data?.result as never[]) || []}
            getOptionLabel={(option: IContact) => option.fullName as string}
            values={selectedCreator}
            onChange={handleCalendarCreatorChange}
            errorMsg={formErrors.calendarCreator}
            label="Creator"
            disabled={isEditMode && !editEnabled}
          />
          <AutocompleteComponent
            id="calendar-type-autocomplete"
            options={(allTypes.data?.result as never[]) || []}
            getOptionLabel={(option: IMeetingType) => option.type as string}
            values={selectedType}
            onChange={handleCalendarTypeChange}
            label="Type"
            errorMsg={formErrors.calendarType}
            disabled={isEditMode && !editEnabled}
          />
          <InputComponent
            id="calendar-description"
            label="Description"
            value={description}
            onChange={handleDescriptionChange}
            disabled={isEditMode && !editEnabled}
            multiline
            rows={3}
          />
          <Box
            sx={{
              display: 'flex',
              columnGap: '20px'
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <EpraDatePicker
                id="create-event-time-picker"
                label="Start Date"
                minDate={new Date()}
                value={startDate}
                disabled={isEditMode && !editEnabled}
                errorMsg={formErrors.startDate}
                onChange={(t) => handleStartDateChange(t)}
                sx={{ flexBasis: '58%' }}
              />
              <EpraTimePicker
                id="create-event-time-picker"
                label="Start Time"
                value={startTime}
                onChange={(t) => handleStartTimeChanged(t)}
                disabled={isEditMode && !editEnabled}
                errorMsg={formErrors.startTime}
                sx={{ flexBasis: '42%' }}
              />
              <EpraDatePicker
                id="calendar-end-date-picker"
                label="End Date"
                minDate={startDate ? startDate : new Date()}
                value={endDate}
                onChange={(d) => handleEndDateChange(d)}
                disabled={isEditMode && !editEnabled}
                errorMsg={formErrors.endDate}
                sx={{ flexBasis: '58%' }}
              />
              <EpraTimePicker
                id="create-event-end-time-picker"
                label="End Time"
                value={endTime}
                onChange={(t) => handleEndTimeChange(t)}
                disabled={isEditMode && !editEnabled}
                errorMsg={formErrors.endTime}
                sx={{ flexBasis: '42%' }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
        {editEnabled && (
          <Box className="centralize" sx={{ marginTop: '70px' }}>
            <CancelButton id="cancel-button" onClick={handleCancel} text="Cancel" />
            <SaveButton
              id="save-button"
              onClick={handleCreateButton}
              disabled={isEditMode && !editEnabled}
              text={isEditMode ? 'Save' : 'Create'}
            />
          </Box>
        )}
      </Box>
    </DialogBox>
  );
};

export default CalendarForm;
